import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./styles/Homepage.css";

// Elegant Eye Icon Component
const EyeIcon = ({ show }) => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
    {show ? (
      <>
        <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" />
      </>
    ) : (
      <>
        <path d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z" />
      </>
    )}
  </svg>
);

const API_URL = "https://salathai.co.tz/mrms/Staff.php";

function Homepage() {
  const [loading, setLoading] = useState(true);
  const [activeLogin, setActiveLogin] = useState("staff");
  const [pin, setPin] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showPin, setShowPin] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 3000);
    return () => clearTimeout(timer);
  }, []);

  // ================= STAFF LOGIN =================
  const handleStaffLogin = async (e) => {
    e.preventDefault();
    setError("");

    try {
      const res = await fetch(`${API_URL}?action=login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });

      const data = await res.json();

      if (!data.token) {
        setError(data.error || "Invalid credentials");
        return;
      }

      localStorage.setItem("token", data.token);
      localStorage.setItem("staff", JSON.stringify(data.staff));
      localStorage.setItem("name", data.staff.full_name);

      switch (data.staff.role) {
        case "Admin":
          navigate("/admin");
          break;
        case "Chef":
          navigate("/chef");
          break;
        case "Cashier":
          navigate("/cashier");
          break;
        case "Waitress":
          navigate("/home");
          break;
        default:
          setError("Role not assigned. Contact admin.");
      }
    } catch {
      setError("Server error. Please try again.");
    }
  };

  // ================= PIN LOGIN =================
  useEffect(() => {
    if (pin.length === 4) loginWithPin();
  }, [pin]);

  const loginWithPin = async () => {
    setError("");

    try {
      const res = await fetch(`${API_URL}?action=login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pin }),
      });

      const data = await res.json();

      if (!data.token) {
        setError("Invalid PIN");
        setPin("");
        return;
      }

      localStorage.setItem("token", data.token);
      localStorage.setItem("staff", JSON.stringify(data.staff));
      localStorage.setItem("name", data.staff.full_name);

      navigate("/home");
    } catch {
      setError("Server error");
      setPin("");
    }
  };

  // Loading animation characters
  const loadingText = "SALATHAI RESTAURANT";
  const loadingChars = loadingText.split("");

  return (
    <div className="homepage">
      {loading ? (
        <div className="loading-screen">
          <div className="loading-container">
            <div className="loading-characters">
              {loadingChars.map((char, index) => (
                <span
                  key={index}
                  className="loading-char"
                  style={{ animationDelay: `${index * 0.07}s` }}
                >
                  {char === " " ? "\u00A0" : char}
                </span>
              ))}
            </div>
            <div className="loading-progress-bar">
              <div className="loading-progress-fill"></div>
            </div>
            <p className="loading-system-name">SALATHAI INFORMATION SYSTEM (SIS)</p>
          </div>
        </div>
      ) : (
        <div className="page-content">
          <div className="login-wrapper">
            {/* Brand Section - Centered */}
            <div className="brand-section">
              <h1 className="brand-title">SALATHAI RESTAURANT</h1>
              <p className="brand-subtitle">SALATHAI INFORMATION SYSTEM <span className="brand-accent">(SIS)</span></p>
            </div>

            {/* Login Card */}
            <div className="login-card">
              <div className="toggle-buttons">
                <button
                  className={activeLogin === "staff" ? "active" : ""}
                  onClick={() => {
                    setActiveLogin("staff");
                    setError("");
                  }}
                >
                  Staff Access
                </button>
                <button
                  className={activeLogin === "pin" ? "active" : ""}
                  onClick={() => {
                    setActiveLogin("pin");
                    setError("");
                  }}
                >
                  Quick PIN
                </button>
              </div>

              {error && <div className="error-message">{error}</div>}

              {activeLogin === "staff" && (
                <form className="login-form" onSubmit={handleStaffLogin}>
                  <div className="input-group">
                    <input
                      type="email"
                      placeholder="Email address"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                    <span className="input-icon">✉</span>
                  </div>
                  <div className="input-group password-group">
                    <input
                      type={showPassword ? "text" : "password"}
                      placeholder="Password"
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                    <span className="input-icon">🔒</span>
                    <button
                      type="button"
                      className="eye-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                      tabIndex={-1}
                    >
                      <EyeIcon show={showPassword} />
                    </button>
                  </div>
                  <button type="submit" className="login-submit">
                    Sign In
                    <span className="btn-arrow">→</span>
                  </button>
                </form>
              )}

              {activeLogin === "pin" && (
                <div className="pin-form">
                  <div className="input-group password-group">
                    <input
                      type={showPin ? "text" : "password"}
                      value={pin}
                      maxLength="4"
                      placeholder="Enter 4-digit PIN"
                      onChange={(e) =>
                        setPin(e.target.value.replace(/[^0-9]/g, ""))
                      }
                      inputMode="numeric"
                      pattern="\d*"
                    />
                    <span className="input-icon">🔑</span>
                    <button
                      type="button"
                      className="eye-toggle"
                      onClick={() => setShowPin(!showPin)}
                      tabIndex={-1}
                    >
                      <EyeIcon show={showPin} />
                    </button>
                  </div>
                  <p className="pin-note">Auto-login after 4 digits</p>
                </div>
              )}

              <div className="login-footer">
                <p className="help-text">Secure access • Contact administrator</p>
              </div>
            </div>
          </div>

          <footer className="footer">
            <p>© 2026 SALATHAI INFORMATION SYSTEM (SIS) — All rights reserved</p>
          </footer>
        </div>
      )}
    </div>
  );
}

export default Homepage;